import { createSlice } from '@reduxjs/toolkit';
import { set, get } from 'lodash-es';
import clone from 'clone';
import formValuesStateMachineReducer from './stateMachines';
export const defaultProduct = {
    id: '',
    name: '',
    code: '',
    description: '',
    note: '',
    offer_text: '',
    steps: '',
    is_hidden: false,
    type: '',
    unit: '',
    cost: '',
    surcharge_bottom_percentage: '',
    max_discount_initial_percentage: '',
    max_discount_override_percentage: '',
    categories: [],
    sub_products: [],
    parent_products: [],
    product_image_id: '',
    surcharge_top_percentage: 0,
    surcharge_bottom_amount: 0,
    surcharge_top_amount: 0,
    price_min_amount: 0,
    price_normal_amount: 0,
    max_discount_initial_amount: 0,
    max_discount_calculated_percentage: 0,
    max_discount_calculated_amount: 0,
    max_discount_percentage: 0,
    max_discount_amount: 0,
};
export const defaultOffer = {
    id: '',
    offer_type: '',
    project_id: '',
    client_id: '',
    buyer_id: '',
    title: '',
    text: '',
    note: '',
    kickback_enabled: false,
    kickback_percentage: 0,
    show_discount: true,
    reference: '',
    expires_at: '',
    updated_at: '',
    offer_date: '',
    follow_up_date: '',
    items: [],
    items_price: 0,
    items_discount: 0,
    items_discounted_price: 0,
    items_tax: 0,
    items_taxed_price: 0,
    items_grand_total: 0,
};
export const defaultContact = {
    id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    clients: [],
};
export const defaultPerson = {
    id: '',
    first_name: '',
    last_name: '',
    email: '',
    second_email: '',
    client_reference_name: '',
    phone: '',
    street_address: '',
    address_line_2: '',
    city: '',
    postcode: '',
    country: '',
};
export const defaultCompany = {
    id: '',
    name: '',
    email: '',
    phone: '',
    street_address: '',
    address_line_2: '',
    city: '',
    postcode: '',
    country: '',
    organization_number: '',
    website: '',
};
export const defaultClient = {
    id: '',
    type: '',
    default_discount_percentage: '0',
    default_assembly_discount_percentage: '0',
    premium_percentage: '0',
    note: '',
    contacts: [],
    clientable: Object.assign(Object.assign({}, defaultPerson), defaultCompany),
};
export const defaultProject = {
    id: '',
    name: '',
    note: '',
    billing_target: '',
    billing_first_name: '',
    billing_last_name: '',
    billing_company: '',
    billing_street_address: '',
    billing_address_line_2: '',
    billing_city: '',
    billing_postcode: '',
    billing_country: '',
    billing_email: '',
    billing_phone: '',
    shipping_target: '',
    shipping_first_name: '',
    shipping_last_name: '',
    shipping_company: '',
    shipping_street_address: '',
    shipping_address_line_2: '',
    shipping_city: '',
    shipping_postcode: '',
    shipping_country: '',
    shipping_email: '',
    shipping_phone: '',
    parent_id: '',
    leader_user_id: '',
    client_id: '',
    contacts: [],
    buyer_id: '',
    buyer_contacts: [],
    parent: null,
    client: null,
    buyer: null,
};
const initialState = {
    product: defaultProduct,
    offer: defaultOffer,
    project: defaultProject,
    person: defaultPerson,
    client: defaultClient,
    company: defaultCompany,
    contact: defaultContact,
};
export const defaultFormValueState = clone(initialState);
function arrayReducer(formsState = [], searchKey, searchValue, name, value) {
    const items = Array.isArray(formsState) ? [...formsState] : [];
    const index = items.findIndex((item) => {
        return searchValue === item[searchKey];
    });
    if (-1 !== index) {
        if (name) {
            items[index] = set(items[index], name, clone(value));
        }
        else {
            items[index] = clone(value);
        }
    }
    return items;
}
export const valuesSlice = createSlice({
    name: 'form-values',
    initialState,
    reducers: {
        setFormValue: (state, action) => {
            const { form, name = null, value = '', fields = null } = action.payload;
            if (name) {
                state[form] = set(get(state, form, {}), name, clone(value));
            }
            if (fields) {
                let fieldChanges = Array.isArray(fields)
                    ? fields
                    : Object.keys(fields).map((name) => {
                        return {
                            name,
                            value: fields[name],
                        };
                    });
                let formValues = state[form] ? state[form] : {};
                for (let index = 0; index < fieldChanges.length; index++) {
                    const fieldChange = fieldChanges[index];
                    set(formValues, fieldChange.name, clone(fieldChange.value));
                }
                state[form] = formValues;
            }
            state = formValuesStateMachineReducer(state, action);
        },
        pushFormValue: (state, action) => {
            const { form, name, value } = action.payload;
            const oldValue = Array.isArray(get(state, `${form}.${name}`))
                ? get(state, `${form}.${name}`)
                : [];
            oldValue.push(clone(value));
            state[form] = set(state[form], name, oldValue);
            state = formValuesStateMachineReducer(state, action);
        },
        setFormArrayObjectValue: (state, action) => {
            const { form, arrayPath, searchKey, searchValue, name, value } = action.payload;
            state[form] = set(state[form], arrayPath, arrayReducer(get(state, `${form}.${arrayPath}`), searchKey, searchValue, name, value));
            state = formValuesStateMachineReducer(state, action);
        },
        removeFormArrayObject: (state, action) => {
            const { form, arrayPath, searchKey, searchValue } = action.payload;
            const newItems = get(state, `${form}.${arrayPath}`, []).filter((item) => {
                return searchValue !== item[searchKey];
            });
            state[form] = set(state[form], arrayPath, newItems);
            state = formValuesStateMachineReducer(state, action);
        },
    },
});
export const { setFormValue, pushFormValue, setFormArrayObjectValue, removeFormArrayObject, } = valuesSlice.actions;
export default valuesSlice.reducer;
